import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Import from "./component";
import LabelerSnackbar from "../common/SnackBar";
import constants from "../../tools/constants";
import withContext from "../../tools/withContext";
import ContextType from "../../types/Context";
import t from "../../locales/t";

interface PropTypes {
  ctx: ContextType;
  match: any;
}

const ImportContainer = (props: PropTypes) => {
  const {
    ctx: { appFetch, user },
    match: {
      params: { projectId, type },
    },
  } = props;
  const {
    PROJECTS_PATH,
    COUNT_ES,
    IMPORT_ES,
    UPLOAD_FILES,
    LIST_STAGGING_PATH,
    LIST_ES_METATDATA,
    UPLOAD_PROPERTIES,
    VIDEOS_INCOMING,
    VIDEOS_DISPATCH,
  } = constants.paths;
  const [project, setProject] = useState<any>({});
  const [uploadProperties, setUploadProperies] = useState<any>(null);
  const [metadatas, setMetadatas] = useState<any[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [numberImages, setNumberImages] = useState<any>(null);
  const [isUploading, setIsUpLoading] = useState(false);
  const [progress, setProgress] = useState<Number>(0);
  const [isError, setIsError] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const uploadCancelRef = useRef<any>(null);

  const fetchDatas = async (cancelled: { current: boolean }) => {
    try {
      setIsError(false);

      // on récupère les batches
      const datas1 = await appFetch(
        `${PROJECTS_PATH}/${projectId}`,
        // , null, null, true
      );
      if (cancelled.current) return;
      setProject(datas1);
      const datas2 = await appFetch(
        `${LIST_STAGGING_PATH}?projectId=${projectId}`,
      );
      if (cancelled.current) return;
      setFolders(datas2);
      if (datas1.dataSource === "ES") {
        const datas3 = await appFetch(
          `${LIST_ES_METATDATA}?projectId=${projectId}`,
        );
        if (cancelled.current) return;
        setMetadatas(datas3);
      }

      const datas4 = await appFetch(UPLOAD_PROPERTIES);
      if (cancelled.current) return;
      setUploadProperies({
        ...datas4,
        "max-file-size": "10MB",
        // "max-request-size": "10GB",
      });
    } catch (e) {
      if (cancelled.current) return;
      //, activated: true
      setIsError(true);
      setMessage(
        // @ts-ignore
        e?.response?.data?.message || e.message || "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
    }
  };

  const onUploadProgress = (e: ProgressEvent) => {
    setProgress(Math.round((e.loaded / e.total) * 100));
  };

  const uploadFiles = async (params: any) => {
    axios.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        console.error("error", error);
        return Promise.reject(error);
      },
    );
    try {
      const { formData, subFolderName } = params;
      setIsError(false);
      setIsUpLoading(true);

      const Authorization = `Bearer ${user.token}`;
      formData.append("projectId", project.id);
      formData.append("destFolder", subFolderName);

      const url = `${process.env.REACT_APP_API_URL}${UPLOAD_FILES}`;

      const source = axios.CancelToken.source();
      uploadCancelRef.current = source;

      await axios.post(url, formData, {
        onUploadProgress,
        cancelToken: source.token,
        headers: {
          Authorization,
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });

      uploadCancelRef.current = null;
      setIsUpLoading(false);
      setMessage(`Import des images terminé !`);
      setIsError(false);
      setIsSnackbarOpen(true);
    } catch (e) {
      uploadCancelRef.current = null;
      setIsUpLoading(false);

      if (axios.isCancel(e)) {
        setProgress(0);
        setIsError(false);
        setMessage("Import annulé");
        setIsSnackbarOpen(true);
        throw e;
      }

      setIsError(true);

      setMessage(
        // @ts-ignore
        (e?.response?.data?.errorKey
          ? // @ts-ignore
            t("error." + e?.response?.data?.errorKey, {
              folderName: params.subFolderName,
            })
          : // @ts-ignore
            e?.response?.data?.message) ?? "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const importEs = async (params: any) => {
    const {
      dateFrom,
      dateTo,
      subFolderName: destFolder,
      projectId,
      filterMetadatas,
    } = params;
    try {
      setIsError(false);
      await appFetch(IMPORT_ES, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          destFolder,
          dateFrom,
          dateTo,
          filterMap: filterMetadatas.reduce(
            (m: any, f: any) => ({ ...m, [f.key]: f.value }),
            {},
          ),
        }),
      });

      setMessage(`Importation ES lancée !`);
      setIsError(false);
      setIsSnackbarOpen(true);
    } catch (e) {
      setIsError(true);
      setMessage(
        // @ts-ignore
        e?.response?.data?.message || e.message || "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const listVideoFiles = async (params: { uploadFolder: string }) => {
    try {
      setIsError(false);
      const data = await appFetch(
        `${VIDEOS_INCOMING}?projectName=${encodeURIComponent(params.uploadFolder)}`,
      );
      return data;
    } catch (e) {
      setIsError(true);
      setMessage(
        // @ts-ignore
        e?.response?.data?.message || e.message || "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const dispatchVideos = async (params: {
    projectName: string;
    uploadFolder: string;
    targetFolder: string;
    fps: number;
    videoNames: string[];
  }) => {
    try {
      setIsError(false);
      
      await appFetch(VIDEOS_DISPATCH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      setMessage(`Importation des vidéos lancée !`);
      setIsError(false);
      setIsSnackbarOpen(true);
    } catch (e) {
      setIsError(true);
      setMessage(
        // @ts-ignore
        (e?.response?.data?.errorKey
          ? // @ts-ignore
            t("error." + e?.response?.data?.errorKey, {
              folderName: params.targetFolder,
            })
          : // @ts-ignore
            e?.response?.data?.message) ?? "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const cancelUpload = () => {
    uploadCancelRef.current?.cancel("Import annulé par l'utilisateur");
  };

  const clearCount = () => setNumberImages(null);

  const countEs = async (params: any) => {
    const { dateFrom, dateTo, projectId, filterMetadatas } = params;
    try {
      setIsError(false);
      setNumberImages(null);
      const nbImages = await appFetch(COUNT_ES, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          destFolder: null,
          dateFrom,
          dateTo,
          filterMap: filterMetadatas.reduce(
            (m: any, f: any) => ({ ...m, [f.key]: f.value }),
            {},
          ),
        }),
      });

      setNumberImages(Number(nbImages));
      setIsError(false);
    } catch (e) {
      console.error(e);
      setIsError(true);
      setMessage(
        // @ts-ignore
        e?.response?.data?.message || e.message || "Une erreur est survenue",
      );
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const closeSnackbar = () => setIsSnackbarOpen(false);

  useEffect(() => {
    const cancelled = { current: false };
    fetchDatas(cancelled);
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <LabelerSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        variant={isError ? "error" : "success"}
        message={
          isError
            ? `Une erreur est survenue ${message ? ` : ${message}` : ""}`
            : message
        }
      />
      {uploadProperties && (
        <Import
          uploadFiles={uploadFiles}
          project={project}
          type={type}
          folders={folders}
          metadatas={metadatas}
          importEs={importEs}
          countEs={countEs}
          numberImages={numberImages}
          clearCount={clearCount}
          progress={progress}
          setProgress={setProgress}
          isUploading={isUploading}
          cancelUpload={cancelUpload}
          uploadProperties={uploadProperties}
          listVideoFiles={listVideoFiles}
          dispatchVideos={dispatchVideos}
        />
      )}
    </React.Fragment>
  );
};

export default withContext(ImportContainer);
